<script setup>
/* eslint-disable no-unused-vars, vue/prefer-separate-static-class, vue/no-static-inline-styles */
import { nextTick, onUnmounted, ref } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useRouter } from 'vue-router';

const router = useRouter();
const { accountScopedRoute } = useAccount();

const channelRoute = subPage =>
  accountScopedRoute('settings_inboxes_page_channel', { sub_page: subPage });

const newInboxRoute = () => accountScopedRoute('settings_inbox_new');

const copy = {
  eyebrow: 'Boas-vindas',
  title: 'Nenhuma caixa de entrada cadastrada',
  subtitle:
    'Para começar a atender, conecte pelo menos um canal. Siga os passos abaixo para registrar sua primeira caixa de entrada.',
  startTour: 'Ver tutorial passo a passo',
  closeTour: 'Fechar tour',
  goTo: 'Abrir',
  previous: 'Voltar',
  next: 'Próximo',
  finish: 'Concluir',
};

const steps = [
  {
    number: '01',
    title: 'Acesse as configurações',
    description:
      'Clique em "Configurações" no menu lateral e selecione "Caixas de entrada" para ver a lista de canais disponíveis.',
    icon: 'i-lucide-settings',
    tone: 'teal',
    action: {
      label: 'Ir para Configurações',
      route: 'settings_inbox_new',
    },
  },
  {
    number: '02',
    title: 'Escolha o canal de comunicação',
    description:
      'Selecione entre WhatsApp Oficial, Instagram, Messenger, ou outros canais. Recomendamos começar pelo WhatsApp.',
    icon: 'i-lucide-message-circle',
    tone: 'blue',
    channels: [
      { label: 'WhatsApp', icon: 'i-lucide-phone', subPage: 'whatsapp' },
      { label: 'Instagram', icon: 'i-lucide-instagram', subPage: 'instagram' },
      {
        label: 'Messenger',
        icon: 'i-lucide-messages-square',
        subPage: 'messenger',
      },
      { label: 'Todos os canais', icon: 'i-lucide-layout-grid', subPage: null },
    ],
  },
  {
    number: '03',
    title: 'Autorize e configure',
    description:
      'Siga as instruções específicas do canal: para WhatsApp, você precisará de um número de telefone verificado na Meta.',
    icon: 'i-lucide-shield-check',
    tone: 'amber',
    action: {
      label: 'Ver documentação',
      href: 'https://talkvia.com.br',
    },
  },
  {
    number: '04',
    title: 'Adicione agentes ao canal',
    description:
      'Após criar a caixa de entrada, adicione os agentes responsáveis pelo atendimento nesse canal.',
    icon: 'i-lucide-users',
    tone: 'grass',
    action: {
      label: 'Criar primeira conexão',
      route: 'settings_inbox_new',
    },
  },
];

const isTourActive = ref(false);
const activeTourStep = ref(0);
const spotlightStyle = ref({});
const tooltipStyle = ref({});

const toneClass = tone =>
  ({
    teal: 'bg-n-teal-3 text-n-teal-11',
    blue: 'bg-n-blue-3 text-n-blue-11',
    amber: 'bg-n-amber-3 text-n-amber-11',
    grass: 'bg-n-grass-3 text-n-grass-11',
    slate: 'bg-n-slate-3 text-n-slate-11',
  })[tone] || 'bg-n-slate-3 text-n-slate-11';

const toneBorder = tone =>
  ({
    teal: 'border-n-teal-6',
    blue: 'border-n-blue-6',
    amber: 'border-n-amber-6',
    grass: 'border-n-grass-6',
    slate: 'border-n-weak',
  })[tone] || 'border-n-weak';

const updateSpotlight = async stepIndex => {
  await nextTick();
  const el = document.querySelector(`[data-guide-step="${stepIndex}"]`);
  if (!el) return;
  const rect = el.getBoundingClientRect();
  const pad = 12;
  spotlightStyle.value = {
    top: `${rect.top - pad}px`,
    left: `${rect.left - pad}px`,
    width: `${rect.width + pad * 2}px`,
    height: `${rect.height + pad * 2}px`,
    borderRadius: '12px',
  };
  const tooltipTop = rect.bottom + pad + 16;
  const tooltipLeft = Math.max(16, rect.left - 60);
  tooltipStyle.value = {
    top: `${tooltipTop}px`,
    left: `${tooltipLeft}px`,
    maxWidth: '360px',
  };
};

const startTour = async () => {
  isTourActive.value = true;
  activeTourStep.value = 0;
  await updateSpotlight(0);
};

const nextStep = async () => {
  if (activeTourStep.value < steps.length - 1) {
    activeTourStep.value += 1;
    await updateSpotlight(activeTourStep.value);
  } else {
    isTourActive.value = false;
  }
};

const prevStep = async () => {
  if (activeTourStep.value > 0) {
    activeTourStep.value -= 1;
    await updateSpotlight(activeTourStep.value);
  }
};

const closeTour = () => {
  isTourActive.value = false;
};

const handleKeydown = e => {
  if (!isTourActive.value) return;
  if (e.key === 'ArrowRight') nextStep();
  if (e.key === 'ArrowLeft') prevStep();
  if (e.key === 'Escape') closeTour();
};

const navigateStep = step => {
  if (step.action?.route) {
    router.push(accountScopedRoute(step.action.route));
  } else if (step.action?.href) {
    window.open(step.action.href, '_blank', 'noopener');
  }
};

const navigateChannel = channel => {
  if (channel.subPage) {
    router.push(channelRoute(channel.subPage));
  } else {
    router.push(newInboxRoute());
  }
};

if (typeof window !== 'undefined') {
  window.addEventListener('keydown', handleKeydown);
  onUnmounted(() => window.removeEventListener('keydown', handleKeydown));
}
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="h-full overflow-y-auto bg-n-background">
    <!-- Hero header -->
    <section class="border-b border-n-weak px-6 py-8">
      <div class="max-w-2xl">
        <div class="mb-3 flex items-center gap-2 text-sm text-n-slate-10">
          <span
            class="grid size-7 place-items-center rounded-lg bg-n-amber-3 text-n-amber-11"
          >
            <span class="i-lucide-inbox size-4" />
          </span>
          <span>{{ copy.eyebrow }}</span>
        </div>
        <h1 class="text-2xl font-semibold text-n-slate-12">{{ copy.title }}</h1>
        <p class="mt-2 text-sm text-n-slate-10">{{ copy.subtitle }}</p>
        <button
          class="mt-4 inline-flex items-center gap-2 rounded-lg bg-n-teal-9 px-4 py-2.5 text-sm font-medium text-white hover:bg-n-teal-10 transition-colors"
          @click="startTour"
        >
          <span class="i-lucide-play size-4" />
          {{ copy.startTour }}
        </button>
      </div>
    </section>

    <!-- Steps grid -->
    <section class="px-6 py-8">
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div
          v-for="(step, idx) in steps"
          :key="step.number"
          :data-guide-step="idx"
          :class="[
            'flex flex-col gap-3 rounded-xl border bg-n-alpha-1 p-5 transition-shadow hover:shadow-md',
            toneBorder(step.tone),
          ]"
        >
          <div class="flex items-start justify-between">
            <div
              :class="[
                'grid size-9 place-items-center rounded-lg',
                toneClass(step.tone),
              ]"
            >
              <span :class="[step.icon, 'size-5']" />
            </div>
            <span class="text-xs font-bold text-n-slate-8">{{
              step.number
            }}</span>
          </div>
          <div>
            <h3 class="font-semibold text-n-slate-12">{{ step.title }}</h3>
            <p class="mt-1 text-sm text-n-slate-10">{{ step.description }}</p>
          </div>

          <!-- Channel quick-links -->
          <div v-if="step.channels" class="flex flex-wrap gap-2 mt-1">
            <button
              v-for="channel in step.channels"
              :key="channel.label"
              class="inline-flex items-center gap-1.5 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-1.5 text-xs font-medium text-n-slate-11 hover:border-n-teal-6 hover:text-n-teal-11 transition-colors"
              @click="navigateChannel(channel)"
            >
              <span :class="[channel.icon, 'size-3.5']" />
              {{ channel.label }}
            </button>
          </div>

          <!-- Action button -->
          <div v-else-if="step.action" class="mt-auto pt-1">
            <button
              class="inline-flex items-center gap-1.5 rounded-lg border border-n-weak px-3 py-1.5 text-xs font-medium text-n-slate-11 hover:border-n-teal-6 hover:text-n-teal-11 transition-colors"
              @click="navigateStep(step)"
            >
              <span class="i-lucide-arrow-right size-3.5" />
              {{ step.action.label }}
            </button>
          </div>
        </div>
      </div>
    </section>
  </div>

  <!-- Tour overlay -->
  <Teleport to="body">
    <div v-if="isTourActive" class="fixed inset-0 z-50" @click.self="closeTour">
      <!-- Dark mask with spotlight cut-out via box-shadow -->
      <div
        class="fixed inset-0 bg-n-slate-12/70"
        style="pointer-events: none"
      />
      <div
        class="fixed border-2 border-n-teal-9 transition-all duration-300"
        style="
          pointer-events: none;
          box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.55);
        "
        :style="spotlightStyle"
      />

      <!-- Tooltip card -->
      <div
        class="fixed z-[51] rounded-xl border border-n-weak bg-n-solid-1 p-4 shadow-xl"
        :style="tooltipStyle"
      >
        <div class="mb-2 flex items-center justify-between gap-4">
          <span class="text-xs font-semibold text-n-teal-11">
            Passo {{ activeTourStep + 1 }} de {{ steps.length }}
          </span>
          <button
            class="text-n-slate-9 hover:text-n-slate-12"
            @click="closeTour"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>
        <h4 class="font-semibold text-n-slate-12">
          {{ steps[activeTourStep].title }}
        </h4>
        <p class="mt-1 text-sm text-n-slate-10">
          {{ steps[activeTourStep].description }}
        </p>
        <div class="mt-4 flex items-center gap-3">
          <button
            v-if="activeTourStep > 0"
            class="rounded-lg border border-n-weak px-3 py-1.5 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
            @click="prevStep"
          >
            {{ copy.previous }}
          </button>
          <button
            class="rounded-lg bg-n-teal-9 px-3 py-1.5 text-xs font-medium text-white hover:bg-n-teal-10 transition-colors"
            @click="nextStep"
          >
            {{ activeTourStep === steps.length - 1 ? copy.finish : copy.next }}
          </button>
        </div>
        <!-- progress dots -->
        <div class="mt-3 flex gap-1.5">
          <div
            v-for="(_, i) in steps"
            :key="i"
            :class="[
              'size-1.5 rounded-full transition-colors',
              i === activeTourStep ? 'bg-n-teal-9' : 'bg-n-slate-6',
            ]"
          />
        </div>
      </div>
    </div>
  </Teleport>
</template>
